<template>
  <div class="bt">
    <header class="bt__header">
      <h1 class="bt__title">Brand typography</h1>
      <p class="bt__intro">
        What the brand kit declares for <strong>{{ kit?.title ?? brand }}</strong> beside the text styles our tokens
        generate. The specimens below are live: they use the <code>.ds-*</code> classes with this brand's tokens
        applied, so they render exactly as a component would.
      </p>
    </header>

    <section class="bt__section">
      <h2 class="bt__section-title">Typefaces</h2>

      <p v-if="!typefaces.length" class="bt__note">
        No brand kit typefaces for this brand. Our own font tokens are listed below.
      </p>

      <ul v-else class="bt__faces">
        <li v-for="face in typefaces" :key="`${face.role}-${face.family}`" class="bt__face">
          <div class="bt__face-head">
            <span class="bt__face-role">{{ face.role }}</span>
            <DriftBadge v-if="face.match" :match="face.match" />
          </div>
          <p class="bt__face-family" :style="styleFor(face)">{{ face.family }}</p>
          <p class="bt__face-pangram" :style="styleFor(face)">{{ PANGRAM }}</p>
          <p class="bt__face-weights">{{ face.weights.join(' · ') || 'no weights listed' }}</p>
          <p v-if="!canRender(face)" class="bt__face-warning">
            Not loaded, so this is the playground's own font rather than a guess at {{ face.family }}. The brand's
            stylesheet declares
            <template v-if="declaredFamilies.length">
              <code>{{ declaredFamilies.join(', ') }}</code> — a different cut, or a different family altogether.
            </template>
            <template v-else>no <code>@font-face</code> at all.</template>
          </p>
          <p v-else-if="face.renderFamily !== face.family" class="bt__face-note">
            declared as <code>{{ face.renderFamily }}</code>
          </p>
          <p v-if="face.tokens.length" class="bt__face-token">
            ours: <code v-for="token in face.tokens" :key="token">{{ token }}</code>
          </p>
        </li>
      </ul>

      <dl class="bt__tokens">
        <div v-for="[name, value] in fontTokens" :key="name" class="bt__token-row">
          <dt><code>--figma-fonts-{{ name }}</code></dt>
          <dd>{{ value }}</dd>
        </div>
      </dl>
    </section>

    <section class="bt__section">
      <h2 class="bt__section-title">Our text styles <span class="bt__count">{{ dsStyles.length }} classes</span></h2>

      <ul class="bt__specimens">
        <li v-for="style in dsStyles" :key="style.name" class="bt__specimen">
          <code class="bt__specimen-name">.{{ style.name }}</code>
          <p :class="style.name" class="bt__specimen-text">
            <template v-if="style.family">{{ style.family }} — </template>{{ PANGRAM }}
          </p>
        </li>
      </ul>
    </section>

    <section v-for="table in styleTables" :key="table.title" class="bt__section">
      <h2 class="bt__section-title">
        {{ table.title }} <span class="bt__count">{{ table.rows.length }} rows</span>
      </h2>

      <p v-if="!table.rows.length" class="bt__note">This brand kit documents no styles here.</p>

      <table v-else class="bt__table">
        <thead>
          <tr>
            <th>Style</th>
            <th>Typeface</th>
            <th>Weight</th>
            <th>Note</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in table.rows" :key="`${row.style}-${index}`">
            <td>{{ row.style }}</td>
            <td>{{ row.typeface }}</td>
            <td>{{ row.weight }}</td>
            <td>{{ row.note ?? '—' }}</td>
          </tr>
        </tbody>
      </table>
    </section>
  </div>
</template>

<script setup lang="ts">
  import typographyCss from '@tapp/design-tokens/figma-variables/typography.css?raw';

  import DriftBadge from '../../figma-playground/DriftBadge.vue';
  import {
    DEFAULT_BRAND,
    extractFontFaceFamilies,
    extractFontTokens,
    extractDeclarations,
    isBrand,
    loadBrandCss,
    packFontName,
    resolveFontFamily,
    resolveTokenValue,
  } from '../../figma-playground/brands';
  import { loadBrandKit } from '../../figma-playground/brand-kits';

  const route = useRoute();
  const brand = computed(() => (isBrand(route.query.brand) ? route.query.brand : DEFAULT_BRAND));

  // Plain refs with a watcher rather than useAsyncData: both sources are local files, and
  // a key interpolated at setup time would let a revisit serve another brand's payload.
  const kit = ref(await loadBrandKit(brand.value));
  const css = ref(await loadBrandCss(brand.value));

  watch(brand, async (name) => {
    const [nextKit, nextCss] = await Promise.all([loadBrandKit(name), loadBrandCss(name)]);
    if (brand.value !== name) return;

    kit.value = nextKit;
    css.value = nextCss;
  });

  /**
   * The generated classes with the font token each one uses, read from the stylesheet the
   * shell already loads — so a new Figma text style appears here without touching this
   * page. Every class resolves its family through a chain of aliases, and which family a
   * style lands on is the thing worth seeing, so each specimen names it.
   */
  const dsClassTokens = [...typographyCss.matchAll(/^\.(ds-[a-z0-9-]+)\s*\{[^}]*?font-family:\s*var\((--[a-z0-9-]+)\)/gm)].map(
    (match) => ({ name: match[1] ?? '', token: match[2] ?? '' }),
  );

  const fontTokens = computed(() => [...extractFontTokens(css.value ?? '')]);

  const dsStyles = computed(() => {
    const declarations = extractDeclarations(css.value ?? '');

    return dsClassTokens.map(({ name, token }) => ({ name, family: resolveTokenValue(declarations, token) }));
  });

  /**
   * A kit typeface counts as ours when one of the brand's `--figma-fonts-*` tokens names
   * the same family. Compared loosely: the kit writes "Anek Latin" where the token says
   * "AnekLatin".
   */
  // Every specimen reads "Family — pangram", so the family is always shown set in itself.
  const PANGRAM = 'The quick brown fox jumps over the lazy dog';

  const declaredFamilies = computed(() => extractFontFaceFamilies(css.value ?? ''));

  const typefaces = computed(() =>
    (kit.value?.typography?.typefaces ?? []).map((face) => {
      const key = packFontName(face.family);
      const tokens = fontTokens.value
        .filter(([, value]) => packFontName(value) === key)
        .map(([name]) => `--figma-fonts-${name}`);

      const renderFamily = resolveFontFamily(face.family, declaredFamilies.value);
      // Resolved from an @font-face this brand declares, so it is certain to render.
      const declared = renderFamily !== face.family || declaredFamilies.value.includes(face.family);

      return {
        ...face,
        tokens,
        renderFamily,
        declared,
        match: tokens.length ? ('exact' as const) : ('missing' as const),
      };
    }),
  );

  /**
   * Families the browser reports as usable without this brand declaring them — system
   * fonts such as Arial, which render perfectly well with no @font-face.
   *
   * Only a positive answer is acted on. `document.fonts.check` is inconsistent for
   * families nothing declares (a fallback can count as available), so treating a true
   * result as permission to style is safe while treating a false one as proof of absence
   * is not — and the @font-face list already tells us that for certain.
   */
  const availability = ref<Record<string, boolean>>({});

  async function checkAvailability() {
    if (!import.meta.client || !document.fonts) return;

    await document.fonts.ready;

    availability.value = Object.fromEntries(
      typefaces.value.map((face) => {
        if (face.declared) return [face.family, true];

        try {
          return [face.family, document.fonts.check(`16px "${face.renderFamily}"`)];
        } catch {
          // An unusable family name cannot be checked; do not claim it is missing.
          return [face.family, true];
        }
      }),
    );
  }

  onMounted(checkAvailability);
  watch(typefaces, checkAvailability);

  /**
   * A family is styled only when something says it will actually render: an @font-face
   * this brand declares, or the browser reporting it usable anyway. Otherwise no binding
   * is emitted at all — asking for `'Univers LT Std Condensed'` when the brand ships
   * `UniversLtStd` renders the default face regardless, and an unresolvable font-family
   * sitting in the markup reads as a bug.
   */
  function canRender(face: { family: string; declared: boolean }) {
    return face.declared || availability.value[face.family] === true;
  }

  function styleFor(face: { family: string; renderFamily: string; declared: boolean }) {
    return canRender(face) ? { fontFamily: `'${face.renderFamily}'` } : undefined;
  }

  const styleTables = computed(() => [
    { title: 'Kit text styles — web', rows: kit.value?.typography?.webStyles ?? [] },
    { title: 'Kit text styles — print', rows: kit.value?.typography?.printStyles ?? [] },
  ]);

  useHead({ title: () => `Typography · ${kit.value?.title ?? brand.value}` });
</script>

<style lang="postcss" scoped>
  .bt__header {
    margin-bottom: 2rem;
  }

  .bt__title {
    margin: 0 0 0.5rem;
    font-size: 1.75rem;
    font-weight: 700;
    letter-spacing: -0.02em;
  }

  .bt__intro {
    max-width: 48rem;
    margin: 0;

    font-size: 0.875rem;
    line-height: 1.6;
    color: #727272;
  }

  .bt__section {
    margin-bottom: 2.5rem;
  }

  .bt__section-title {
    display: flex;
    gap: 0.75rem;
    align-items: baseline;

    margin: 0 0 0.75rem;

    font-size: 0.6875rem;
    font-weight: 700;
    color: #929292;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .bt__count {
    font-weight: 400;
    text-transform: none;
    letter-spacing: 0;
  }

  .bt__note {
    max-width: 44rem;
    margin: 0 0 1rem;

    font-size: 0.8125rem;
    line-height: 1.6;
    color: #727272;
  }

  .bt__faces {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
    gap: 0.75rem;

    margin: 0 0 1.25rem;
    padding: 0;

    list-style: none;
  }

  .bt__face {
    padding: 1rem;
    border: 1px solid #e4e4e4;
    border-radius: 0.5rem;
  }

  .bt__face-head {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: center;

    margin-bottom: 0.5rem;
  }

  .bt__face-role {
    font-size: 0.6875rem;
    font-weight: 700;
    color: #929292;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .bt__face-family {
    margin: 0 0 0.375rem;
    font-size: 1.5rem;
  }

  .bt__face-pangram {
    margin: 0 0 0.5rem;
    font-size: 0.9375rem;
    line-height: 1.4;
    color: #545454;
  }

  .bt__face-warning {
    margin: 0 0 0.375rem;
    font-size: 0.6875rem;
    line-height: 1.5;
    color: #b4442c;

    & code {
      margin-right: 0.25rem;
      font-family: 'SF Mono', 'Fira Code', Consolas, monospace;
    }
  }

  .bt__face-note {
    margin: 0 0 0.375rem;
    font-size: 0.6875rem;
    color: #929292;

    & code {
      font-family: 'SF Mono', 'Fira Code', Consolas, monospace;
    }
  }

  .bt__face-weights {
    margin: 0 0 0.375rem;
    font-size: 0.75rem;
    color: #727272;
  }

  .bt__face-token {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;

    margin: 0;

    font-size: 0.6875rem;
    color: #929292;
  }

  .bt__tokens {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
    gap: 0.5rem;
    margin: 0;
  }

  .bt__token-row {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;

    padding: 0.5rem 0.75rem;
    border: 1px solid #f2f2f2;
    border-radius: 0.375rem;

    & dt code {
      font-family: 'SF Mono', 'Fira Code', Consolas, monospace;
      font-size: 0.6875rem;
      color: #727272;
    }

    & dd {
      margin: 0;
      font-size: 0.875rem;
      font-weight: 600;
    }
  }

  .bt__specimens {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    margin: 0;
    padding: 0;

    list-style: none;
  }

  .bt__specimen {
    display: grid;
    grid-template-columns: 11rem 1fr;
    gap: 1rem;
    align-items: baseline;

    padding-bottom: 0.75rem;
    border-bottom: 1px solid #f2f2f2;
  }

  .bt__specimen-name {
    font-family: 'SF Mono', 'Fira Code', Consolas, monospace;
    font-size: 0.6875rem;
    color: #929292;
  }

  .bt__specimen-text {
    margin: 0;
    overflow-wrap: anywhere;
  }

  .bt__table {
    border-collapse: collapse;
    width: 100%;
    max-width: 44rem;
    font-size: 0.8125rem;

    & th,
    & td {
      padding: 0.375rem 0.75rem;
      text-align: left;
    }

    & th {
      font-size: 0.6875rem;
      font-weight: 600;
      color: #929292;
      text-transform: uppercase;
      letter-spacing: 0.04em;

      background: #fafafa;
    }

    & td {
      border-top: 1px solid #f2f2f2;
      color: #545454;
    }
  }
</style>
